import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CircleNotch } from '@phosphor-icons/react'
import type { GameAbility } from '@/types'

interface EditAbilityDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  abilityData: GameAbility | null
  onSave: (abilityData: Omit<GameAbility, 'id'> & { id?: string }) => Promise<void>
}

// Emojis comuns para habilidades
const COMMON_EMOJIS = [
  '✨', '🔥', '❄️', '⚡', '🌪️', '🌊', '🌱', '☀️',
  '🌙', '💫', '🛡️', '⚔️', '🏹', '🪄', '🔮', '📖',
  '🧠', '👁️', '🗣️', '🦅', '🐺', '💨', '🕸️', '💚',
]

export function EditAbilityDialog({ open, onOpenChange, abilityData, onSave }: EditAbilityDialogProps) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    icon: '✨',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  const isEditing = !!abilityData

  useEffect(() => {
    if (abilityData) {
      setFormData({
        id: abilityData.id,
        name: abilityData.name,
        description: abilityData.description,
        icon: abilityData.icon,
      })
    } else {
      setFormData({ id: '', name: '', description: '', icon: '✨' })
    }
  }, [abilityData, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const abilityId = formData.id || formData.name.toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')

    setIsSaving(true)
    try {
      await onSave({
        ...(isEditing && { id: formData.id }),
        ...(!isEditing && { id: abilityId }),
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao salvar habilidade:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Habilidade' : 'Nova Habilidade'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Modifique as informações da habilidade do catálogo.'
                : 'Crie uma habilidade para o catálogo da campanha.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Ícone */}
            <div className="space-y-2">
              <Label htmlFor="icon">Ícone</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="text-2xl w-16 h-16"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  {formData.icon}
                </Button>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Cole um emoji aqui"
                  className="flex-1"
                />
              </div>

              {showEmojiPicker && (
                <div className="grid grid-cols-8 gap-1 p-2 border rounded-md bg-background">
                  {COMMON_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="text-2xl p-2 hover:bg-accent rounded transition-colors"
                      onClick={() => {
                        setFormData({ ...formData, icon: emoji })
                        setShowEmojiPicker(false)
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Habilidade *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Bola de Fogo, Cura Menor..."
                required
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva o que a habilidade faz..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <CircleNotch size={16} className="mr-2 animate-spin" />}
              {isEditing ? 'Salvar Alterações' : 'Criar Habilidade'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
